import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

// MUI Components
import {
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar
} from '@mui/material';

// MUI Icons
import {
  ContentCopy as ContentCopyIcon,
  Construction as ConstructionIcon,
  Download as DownloadIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

// Contexts
import { useTheme } from '../contexts/ThemeContext';

// Inputs
import CustomDate from './Inputs/CustomDate';

const WalmartErrorHandler = ({ errors, body, openShipmentsModal, handleConvertToWFSUsingSC, getItemID, createInboundOrder, close }) => {
  const { theme } = useTheme();

  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
  const [actualErrors, setActualErrors] = useState(errors || []);

  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(dayjs().add(14, 'days'));
  const [groupedErrors, setGroupedErrors] = useState([]);
  const [otherErrors, setOtherErrors] = useState([]);
  const [affectedSkus, setAffectedSkus] = useState([]);
  const [errorBody, setErrorBody] = useState({});
  const [errorCounts, setErrorCounts] = useState({ 'ERROR': 0, 'WARN': 0, 'INFO': 0 });
  const [errorDialog, setErrorDialog] = useState({ open: false, message: "", actions: [], input: undefined, note: undefined });

  const groupErrorsByOrderItem = (errors, orderItems) => {
    const grouped = orderItems?.map(item => ({
      sku: item.sku,
      errors: errors.filter(err => getSku(body, err.field) === item.sku)
    }));

    // Separate other errors that are not related to orderItems
    const otherErrors = errors.filter(err => !getSku(body, err.field));

    setGroupedErrors(grouped);
    setOtherErrors(otherErrors);
  };
  const countBySeverity = (errs) => {
    const counts = { 'ERROR': 0, 'WARN': 0, 'INFO': 0 };

    if (errs) {
      errs.forEach(error => {
        counts[error.severity]++;
      })
    }

    setErrorCounts(counts);
  };
  const getAffectedSkus = (errors, body) => {
    const skus = new Set();

    errors.forEach(err => {
      const sku = getSku(body, err.field);
      if (sku) skus.add(sku);
    });

    setAffectedSkus(Array.from(skus));
  };

  // Dialog & Snackbar
  const closeDialog = () => {
    setErrorDialog({ open: false, message: "", actions: [], input: undefined });
  };
  const showAlert = (severity, message) => {
    setSnackbar({ open: true, severity, message });

    // Auto-close after 3 seconds
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 3000);
  };

  // Helpers
  const handleCopyErrors = () => {
    navigator.clipboard.writeText(`Create IO request failed: ${JSON.stringify(errors)}`);
  };
  const generateErrorReport = (errors, body) => {
    let report = "Walmart API Error Report\n";
    report += "======================\n\n";
    report += `Date: ${new Date().toISOString()}\n\n`;

    if (body.inboundOrderId) {
      report += `Inbound Order ID: ${body.inboundOrderId}\n`;
    }

    report += "\nError Summary:\n";
    const counts = countBySeverity(errors);
    report += `${counts.ERROR} Errors, ${counts.WARN} Warnings, ${counts.INFO} Infos\n\n`;

    report += "Detailed Errors:\n";
    report += "--------------\n\n";

    errors.forEach((err, index) => {
      report += `[${index + 1}] Severity: ${err.severity}\n`;
      report += `Category: ${err.category}\n`;
      report += `Code: ${err.code}\n`;
      report += `Field: ${err.field}\n`;
      report += `Info: ${err.info}\n`;
      report += `Description: ${err.description}\n`;

      const sku = getSku(body, err.field);
      if (sku) {
        report += `SKU: ${sku}\n`;
      }

      const value = getValue(body, err.field);
      if (value) {
        report += `Value: ${JSON.stringify(value)}\n`;
      }

      report += "\n";
    });

    report += "\nFull Request Body:\n";
    report += "----------------\n";
    report += JSON.stringify(body, null, 2);

    return report;
  };
  const downloadErrorReport = (errors, body) => {
    alert("The error has been reported to the team, and we are working on resolving it.")
    const report = generateErrorReport(errors, body);
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `walmart-error-report-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleDownloadReport = () => {
    downloadErrorReport(errors, body);
  };
  const showFixOptions = ({ message, actions, input, note }) => {
    setErrorDialog({ open: true, message, actions, input, note });
  };
  const handleFixError = (error, sku) => {
    if (error.code === "404.IQS_SERVICE.100") {
      showFixOptions({
        message: "Item is not WFS Eligible. Choose an action:",
        note: "Walmart may take a few minutes to update item status after conversion. If the issue persists, please try one of the actions below.",
        actions: [
          { label: "Convert to WFS", action: () => convertToWFS(error, sku) },
          { label: "Remove from Order", action: () => removeFromOrder(error, sku) },
          { label: "Convert to WFS using SC", action: () => convertToWFSUsingSC(error, sku) },
        ],
      });
    } else if (error.code === "400.WFS.100") {
      if (error.field.includes("expectedDeliveryDate")) {
        showFixOptions({
          message: error.info,
          actions: [{ label: "Change Expected Delivery Date", action: () => changeExpectedDate(error) }],
          input: true,
        });
      } else if (error.field.includes("returnAddress")) {
        showFixOptions({
          message: error.info,
          actions: [{ label: "Retry", action: () => { openShipmentsModal(); closeDialog(); } },],
        });
      } else {
        showAlert("error", "Sorry, no fixes yet!");
      }
    } else if (error.code === "500.IQS_SERVICE.200") {
      showFixOptions({
        message: error.info,
        actions: [{ label: "Remove from Order", action: () => removeFromOrder(error, sku) },],
      });
    } else {
      showAlert("error", "Sorry, no fixes yet!");
    }
  };
  const removeErrorsByFieldPattern = (errors, fieldPattern) => {
    return errors.filter(error =>
      !error.field.includes(fieldPattern)
    );
  }

  // Errors Data
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'INFO':
        return <InfoIcon sx={{ color: theme.color.primary, marginRight: 1 }} />;
      case 'WARN':
        return <WarningAmberIcon sx={{ color: theme.accent.warning, marginRight: 1 }} />;
      case 'ERROR':
        return <ErrorIcon sx={{ color: theme.accent.error, marginRight: 1 }} />;
      default:
        return null;
    }
  };
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'INFO':
        return `${theme.color.primary}15`;
      case 'WARN':
        return `${theme.accent.warning}15`;
      case 'ERROR':
        return `${theme.accent.error}15`;
      default:
        return '#f5f5f5';
    }
  };
  const getValue = (obj, path) => {
    if (!path) return undefined;
    return path
      .replace(/\[(\d+)\]/g, '.$1') // Convert array index notation to dot notation
      .split('.')                   // Split into keys
      .reduce((o, key) => (o && o[key] !== undefined ? o[key] : undefined), obj);
  };
  const getSku = (body, fieldPath) => {
    if (!fieldPath || !fieldPath.includes('orderItems')) return null;

    // Extract the index from orderItems[index]
    const match = fieldPath.match(/orderItems\[(\d+)\]/);
    if (!match) return null;

    const index = parseInt(match[1]);
    if (body.orderItems && body.orderItems[index] && body.orderItems[index].sku) {
      return body.orderItems[index].sku;
    }
    return null;
  };
  const renderError = (err, body, index) => {
    const field = err.field;
    const value = getValue(body, field);
    const sku = getSku(body, field);

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 2,
          padding: 1.5,
          borderRadius: 1,
          backgroundColor: getSeverityColor(err.severity)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '80%' }}>
          <Box sx={{ marginRight: 1, marginTop: 0.5 }}>
            {getSeverityIcon(err.severity)}
          </Box>
          <Box>
            <Typography sx={{ textAlign: 'left', fontWeight: 'medium', marginBottom: 0.5, fontFamily: theme.fontFamily, color: theme.text.primary, }}>
              {err.info || err.description}
            </Typography>

            {field && (
              <Typography variant="body2" sx={{ textAlign: 'left', fontFamily: theme.fontFamily, color: theme.accent.disabled, }}>
                Field: {field}
              </Typography>
            )}

            {sku && (
              <Typography variant="body2" sx={{ textAlign: 'left', fontFamily: theme.fontFamily, color: theme.accent.disabled, }}>
                SKU: {sku}
              </Typography>
            )}

            {value && typeof value !== 'object' && (
              <Typography variant="body2" sx={{ textAlign: 'left', fontFamily: theme.fontFamily, color: theme.accent.disabled, }}>
                Value: {String(value)}
              </Typography>
            )}

            {err.code && (
              <Typography variant="body2" sx={{ textAlign: 'left', fontFamily: theme.fontFamily, color: theme.accent.disabled, }}>
                Code: {err.code}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={() => handleFixError(err, sku)} sx={{ height: 'fit-content', color: theme.color.primary, }}>
          <ConstructionIcon />
        </IconButton>
      </Box>
    );
  };

  // Fix functions
  const convertToWFSUsingSC = (error, sku) => {
    handleConvertToWFSUsingSC(sku);
    setActualErrors(prevErrors =>
      removeErrorsByFieldPattern(prevErrors, error.field)
    );
    closeDialog();
  };
  const convertToWFS = (error, sku) => {
    const itemId = getItemID(sku);
    if (!itemId) {
      showAlert("error", "Sorry, can't fix!");
      return;
    }
    const url = `https://www.inventoryfalcon.com/mobile-form?itemId=${itemId}`;

    // Define mobile-like window dimensions
    const windowWidth = 480;
    const windowHeight = 844;

    // Calculate screen center position
    const leftPosition = (window.screen.width - windowWidth) / 2;
    const topPosition = (window.screen.height - windowHeight) / 2;

    // Create overlay to disable parent page
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0,0,0,0.5)';
    overlay.style.zIndex = '99999999';
    overlay.style.display = 'flex';
    overlay.style.justifyContent = 'center';
    overlay.style.alignItems = 'center';
    overlay.innerHTML = '<div style="background:white; padding:20px; text-align:center;">Please complete the popup</div>';
    document.body.appendChild(overlay);

    // Disable interactions on the main page
    document.body.style.pointerEvents = 'none';
    overlay.style.pointerEvents = 'auto';

    // Open the popup window
    const popup = window.open(
      url,
      'PopupWindow',
      `width=${windowWidth},` +
      `height=${windowHeight},` +
      `left=${leftPosition},` +
      `top=${topPosition},` +
      'resizable=no,' +
      'scrollbars=yes'
    );

    // Check if popup was blocked
    if (!popup) {
      document.body.style.pointerEvents = 'auto';
      document.body.removeChild(overlay);
      showAlert("warning", "Sorry, can't fix!");

      closeDialog();
      return;
    }

    // Focus on the new window
    popup.focus();

    // Create a timer to check if the popup is closed
    const checkPopupClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopupClosed);

        // Re-enable interactions on the main page
        document.body.style.pointerEvents = 'auto';

        // Remove the overlay
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);

          setActualErrors(prevErrors =>
            removeErrorsByFieldPattern(prevErrors, error.field)
          );
          closeDialog();
        }
      }
    }, 500); // Check every 500ms
  }
  const removeFromOrder = (error, sku) => {
    const orderItems = errorBody.orderItems.filter(item => item.sku !== sku);
    setErrorBody({ ...errorBody, orderItems: orderItems });

    setActualErrors(prevErrors =>
      removeErrorsByFieldPattern(prevErrors, error.field)
    );

    showAlert("success", "Item removed successfully from order!");

    closeDialog();
  };
  const changeExpectedDate = (error) => {
    const formattedDate = dayjs(expectedDeliveryDate).toISOString();
    if (dayjs(expectedDeliveryDate).isBefore(dayjs())) {
      showAlert("error", "Expected delivery date can't be in the past.");
      closeDialog();
      return;
    }
    const orderItems = errorBody.orderItems.map((item) => { return { ...item, expectedDeliveryDate: formattedDate, } });
    setErrorBody({ ...errorBody, orderItems: orderItems });

    setActualErrors((prev) => prev.filter(err => err.info !== error.info));

    showAlert("success", "Expected delivery date Change successfully for all items!");

    closeDialog();
  };

  // Try ship again
  const retryShipment = async () => {
    close();
    setErrorBody({});
    setActualErrors([]);
    createInboundOrder(errorBody);
  };

  useEffect(() => {
    setErrorBody(errorBody || body);
    groupErrorsByOrderItem(actualErrors, (errorBody || body).orderItems);
    countBySeverity(actualErrors);
    getAffectedSkus(actualErrors, errorBody || body);
  }, [actualErrors, errorBody]);

  useEffect(() => {
    setErrorBody(body);
  }, [body]);

  const styles = {
    container: {
      maxHeight: '60vh',
      overflow: 'auto',
      p: '10px',
      '&::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary,
        borderRadius: '15px',
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar,
        borderRadius: '15px',
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover,
      },
      '&::-webkit-scrollbar-button': {
        display: 'none',
      },
    },
    chip: {
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      fontSize: 12
    },
    dateInputLabel: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '&.Mui-focused': {
        color: theme.color.primary, // Change label color on focus
      },
    },
    dateOutlinedInput: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.primary,
      },
    },
  };

  return (
    <Box sx={styles.container}>
      <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 'bold', fontFamily: theme.fontFamily, color: theme.text.primary, }}>
        Walmart Inbound Shipment Errors
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'center' }}>
        <Box>
          <Typography variant="body2" sx={{ marginBottom: 1, fontFamily: theme.fontFamily, color: theme.text.primary, }}>
            Error Summary:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {errorCounts.ERROR > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={<Typography variant="body2" sx={styles.chip}>{errorCounts.ERROR} Errors</Typography>}
                sx={{ color: theme.text.negative, bgcolor: theme.accent.error }}
                size="small"
                color="error"
              />
            )}
            {errorCounts.WARN > 0 && (
              <Chip
                icon={<WarningAmberIcon />}
                label={<Typography variant="body2" sx={styles.chip}>{errorCounts.WARN} Warnings</Typography>}
                sx={{ color: theme.text.negative, bgcolor: theme.accent.warning }}
                size="small"
                color="warning"
              />
            )}
            {errorCounts.INFO > 0 && (
              <Chip
                icon={<InfoIcon />}
                label={<Typography variant="body2" sx={styles.chip}>{errorCounts.INFO} Info</Typography>}
                sx={{ color: theme.text.negative, bgcolor: theme.color.primary }}
                size="small"
                color="info"
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyErrors}
            sx={{
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              border: `1px solid ${theme.border.light}`,
              borderRadius: '10px',
              color: theme.color.primary,
            }}
          >
            Copy Errors
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            sx={{
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              border: `1px solid ${theme.border.light}`,
              borderRadius: '10px',
              color: theme.text.negative,
              backgroundColor: theme.color.primary,
            }}
          >
            Download Report
          </Button>
        </Box>
      </Box>

      {affectedSkus.length > 0 && (
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="body2" sx={{ marginBottom: 1, fontFamily: theme.fontFamily, color: theme.text.primary, }}>
            Affected SKUs:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {affectedSkus.map((sku, index) => (
              <Chip
                key={index}
                label={<Typography variant="body2" sx={{ ...styles.chip, color: theme.text.primary, }}>{sku}</Typography>}
                size="small"
                sx={{
                  backgroundColor: theme.accent.disabled
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ marginY: 2 }} />

      <Typography sx={{ marginBottom: 1, fontFamily: theme.fontFamily, color: theme.text.primary, }}>
        Please review and fix the following issues:
      </Typography>

      {/* Data errors */}
      {groupedErrors && groupedErrors.length > 0 && groupedErrors.map((item, index) => {
        if (item.errors.length > 0) {
          return (
            <Accordion key={index} sx={{ bgcolor: theme.background.secondary }}>
              <AccordionSummary sx={{ borderBottom: `2px solid ${theme.border.light}`, }} expandIcon={<ExpandMoreIcon sx={{ color: theme.text.primary }} />}>
                <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
                  SKU: {item.sku} ({item.errors.length} Errors)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {item.errors.map((err, errIndex) =>
                    renderError(err, errorBody, `data-err-${errIndex}`)
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )
        } else {
          return null;
        }
      })}

      {/* Render Other Errors */}
      {otherErrors && otherErrors.length > 0 && (
        <Accordion sx={{ bgcolor: theme.background.secondary, }}>
          <AccordionSummary sx={{ borderBottom: `2px solid ${theme.border.light}`, }} expandIcon={<ExpandMoreIcon sx={{ color: theme.text.primary }} />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ErrorIcon sx={{ color: theme.accent.error, marginRight: 1 }} />
              <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>Other Errors ({otherErrors.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {otherErrors.map((err, index) =>
                renderError(err, errorBody, `other-err-${index}`)
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Box sx={{ marginTop: 3 }}>
        <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, color: theme.accent.disabled, }}>
          If you need assistance, please contact support at team@inventoryfalcon.com with this error report.
        </Typography>
      </Box>

      <Button
        variant="contained"
        size="small"
        onClick={retryShipment}
        disabled={errorCounts.ERROR > 0}
        sx={{
          textTransform: 'none',
          fontFamily: theme.fontFamily,
          border: `1px solid ${theme.border.light}`,
          borderRadius: '10px',
          color: theme.text.negative,
          backgroundColor: theme.accent.success,
        }}
      >
        {errorCounts.ERROR > 0 ? "Fix Errors before retry" : "Create Inbound Shippment"}
      </Button>

      <Dialog open={errorDialog.open} onClose={closeDialog} sx={{ '& .MuiDialog-paper': { backgroundColor: theme.background.primary, } }}>
        <DialogTitle sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>Error Fix</DialogTitle>
        <DialogContent sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>
          {errorDialog.message}
          {errorDialog?.note && <>
            <Box sx={{ fontFamily: theme.fontFamily, color: theme.text.tertiary, my: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <InfoIcon />{errorDialog?.note}
            </Box>
          </>}
          {errorDialog.input && (
            <CustomDate
              value={expectedDeliveryDate}
              onChange={(newValue) => {
                // Ensure newValue is converted to dayjs if it isn't already
                const dayjsValue = dayjs(newValue);
                setExpectedDeliveryDate(dayjsValue);
              }}
              label="Expected Delivery Date"
            />
          )}
        </DialogContent>
        <DialogActions>
          {errorDialog.actions.map((action, i) => (
            <Button
              key={i}
              variant="contained"
              size="small"
              sx={{
                textTransform: 'none',
                fontFamily: theme.fontFamily,
                border: `1px solid ${theme.border.light}`,
                borderRadius: '10px',
                color: theme.text.negative,
              }}
              onClick={action.action}
            >
              {action.label}
            </Button>
          ))}
          <Button
            variant="outlined"
            size="small"
            sx={{
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              border: `1px solid ${theme.border.light}`,
              borderRadius: '10px',
              color: theme.color.primary,
            }}
            onClick={closeDialog}
          >Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%", zIndex: 999, fontFamily: theme.fontFamily }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default WalmartErrorHandler;